import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  CircularProgress,
  Collapse,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import {
  ALERTS_PAGE_SIZE,
  EMAIL_BATCH_STATUSES,
  PUSH_STATUSES,
} from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardFilters";
import { AlertsStat } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardDrilldown";
import { AlertsMetricInfo } from "../../../../../../models/Dashboard/AlertsDashboard/AlertsDashboardSummary";
import {
  AlertsDashboardStore,
  AlertsListView,
  sortWhitelistForView,
} from "../../../../stores/dashboards/AlertsDashboardStore";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";
import AlertsDataTable from "./AlertsDataTable";
import { alertsRangeSlug, formatAlertsStat } from "./alertsDashboardFormat";

const visibleRange = (
  page: number,
  pageSize: number,
  total: number,
  rowCount: number,
): { from: number; to: number } => {
  if (total <= 0 || rowCount <= 0) {
    return { from: 0, to: 0 };
  }
  const from = (Math.max(page, 1) - 1) * Math.max(pageSize, 1) + 1;
  return { from, to: from + rowCount - 1 };
};

export const AlertsStatCards = ({ stats }: { stats: AlertsStat[] }) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;
  if (stats.length === 0) {
    return null;
  }
  return (
    <Grid2 container spacing={1.5}>
      {stats.map((stat, index) => (
        <Grid2 key={`${stat.label}-${index}`} size={{ xs: 6, sm: 4, md: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              height: "100%",
              borderRadius: 2.5,
              bgcolor: customColors.white,
              border: `1px solid ${customColors.softCardBorder}`,
              borderLeft: `4px solid ${customColors.darkBlue}`,
              boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
            }}
          >
            <CustomTypography
              variant="body4"
              sx={{
                color: customColors.mutedSlateText,
                fontSize: "0.7rem",
                fontWeight: 600,
                letterSpacing: "0.02em",
                textTransform: "uppercase",
              }}
            >
              {stat.label}
            </CustomTypography>
            <CustomTypography
              variant="heading5"
              sx={{
                mt: 0.5,
                color: customColors.navyText,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                wordBreak: "break-word",
              }}
            >
              {formatAlertsStat(stat.value)}
            </CustomTypography>
          </Paper>
        </Grid2>
      ))}
    </Grid2>
  );
};

/** Collapsible "How this is calculated" block from the metric's `how`. */
export const AlertsHowBlock = ({ how }: { how: AlertsMetricInfo | null }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const customColors = theme.palette.customColors;
  const [open, setOpen] = useState(true);
  if (!how?.info?.trim() && !(how?.fields && how.fields.length > 0)) {
    return null;
  }
  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        overflow: "hidden",
        boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
      }}
    >
      <Box
        onClick={() => setOpen((current) => !current)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1,
          cursor: "pointer",
          bgcolor: customColors.white,
          "&:hover": { bgcolor: customColors.hoverGray },
        }}
      >
        <CustomTypography variant="heading7" sx={{ color: colors.title }}>
          How this is calculated
        </CustomTypography>
        <IconButton size="small" aria-label={open ? "Collapse" : "Expand"}>
          {open ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            px: 2,
            pb: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {how.info ? (
            <CustomTypography
              variant="body4"
              sx={{
                color: customColors.navyText,
                whiteSpace: "pre-wrap",
                fontSize: "0.8rem",
              }}
            >
              {how.info}
            </CustomTypography>
          ) : null}
          {how.fields && how.fields.length > 0 ? (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 1,
                bgcolor: customColors.veryLightGray,
                border: `1px solid ${customColors.lightGray}`,
                overflowX: "auto",
              }}
            >
              <CustomTypography
                variant="body4"
                sx={{
                  color: customColors.navyText,
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  fontSize: "0.75rem",
                }}
              >
                {how.fields.join(" · ")}
              </CustomTypography>
            </Box>
          ) : null}
        </Box>
      </Collapse>
    </Paper>
  );
};

export const AlertsEmptyBox = ({ message }: { message: string }) => {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="8rem"
      textAlign="center"
      border="1px dashed"
      borderColor="divider"
      borderRadius={2}
      px={2}
    >
      <CustomTypography
        variant="heading6"
        fontWeight={500}
        color={theme.palette.customColors.greyTextColor}
      >
        {message}
      </CustomTypography>
    </Box>
  );
};

interface AlertsListPanelProps {
  store: AlertsDashboardStore;
  view: AlertsListView;
  maxHeight: number;
}

/**
 * One paged list (drill-down, emails, push or recipient): stat cards, how block,
 * status toggles, Records + Export CSV, showing line and the 11px table.
 */
const AlertsListPanel: React.FC<AlertsListPanelProps> = ({
  store,
  view,
  maxHeight,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const customColors = theme.palette.customColors;
  const data = view.result?.data ?? null;
  const table = data?.table ?? null;
  const rows = table?.rows ?? [];
  const page = table?.page ?? view.page;
  const pageSize = table?.page_size ?? ALERTS_PAGE_SIZE;
  const total = table?.total ?? 0;
  const range = visibleRange(page, pageSize, total, rows.length);
  const statusOptions: readonly string[] =
    view.kind === "emails"
      ? EMAIL_BATCH_STATUSES
      : view.kind === "push"
        ? PUSH_STATUSES
        : [];

  const toggleStatus = (status: string | null) => {
    try {
      if (status === null) {
        void store.changeListStatuses(view.id, []);
        return;
      }
      const next = view.statuses.includes(status)
        ? view.statuses.filter((item) => item !== status)
        : [...view.statuses, status];
      void store.changeListStatuses(view.id, next);
    } catch (error) {
      console.error(error);
    }
  };

  const handleExport = () => {
    try {
      const slug = (view.metricId || view.kind).replace(/[^a-z0-9_]+/gi, "_");
      const dates = alertsRangeSlug(
        store.appliedFilters.start_date,
        store.appliedFilters.end_date,
      );
      void store.exportCsv(view.id, `alerts-${slug}-${dates}.csv`);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowClick = (row: Record<string, unknown>) => {
    try {
      if (view.kind === "emails") {
        void store.openEmail(Number(row.batch_id));
        return;
      }
      void store.openAlert(
        Number(row.alert_id),
        typeof row.title === "string" ? row.title : null,
      );
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {view.error ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 2, border: `1px solid ${colors.infoBorder}` }}
        >
          {view.error}
        </Alert>
      ) : null}
      {view.exportError ? (
        <Alert
          severity="error"
          sx={{ borderRadius: 2, border: `1px solid ${colors.infoBorder}` }}
        >
          {view.exportError}
        </Alert>
      ) : null}

      {view.loading && !data ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 220,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      ) : null}

      {data ? <AlertsStatCards stats={data.stats ?? []} /> : null}
      {data || view.how ? <AlertsHowBlock how={data?.how ?? view.how} /> : null}

      {statusOptions.length > 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <CustomTypography
            variant="heading7"
            sx={{ color: colors.title, mr: 0.5 }}
          >
            Status
          </CustomTypography>
          <AppButton
            label="All"
            variant={
              view.statuses.length === 0 ? "primary" : "transparentPrimary"
            }
            onClick={() => toggleStatus(null)}
            disabled={view.loading}
            customStyles={{ minHeight: 32, px: 1.5 }}
            fontSize="0.75rem"
          />
          {statusOptions.map((status) => (
            <AppButton
              key={status}
              label={status}
              variant={
                view.statuses.includes(status)
                  ? "primary"
                  : "transparentPrimary"
              }
              onClick={() => toggleStatus(status)}
              disabled={view.loading}
              customStyles={{ minHeight: 32, px: 1.5 }}
              fontSize="0.75rem"
            />
          ))}
        </Box>
      ) : null}

      {data ? (
        <>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              flexWrap: "wrap",
            }}
          >
            <CustomTypography variant="heading7" sx={{ color: colors.title }}>
              Records
            </CustomTypography>
            <AppButton
              variant="primary"
              label={view.exportLoading ? "Exporting..." : "Export CSV"}
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              disabled={view.exportLoading || view.loading}
              customStyles={{ minHeight: 32 }}
              fontSize="0.75rem"
            />
          </Box>
          <CustomTypography
            variant="body4"
            sx={{
              color: customColors.mutedSlateText,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {total <= 0
              ? "Showing 0 of 0"
              : `Showing ${range.from}–${range.to} of ${total}`}
          </CustomTypography>
          {rows.length === 0 && !view.loading ? (
            <AlertsEmptyBox message="No records for this view." />
          ) : rows.length > 0 ? (
            <Box sx={{ position: "relative" }}>
              {view.loading ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: `${customColors.white}99`,
                    zIndex: 31,
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              ) : null}
              <AlertsDataTable
                maxHeight={maxHeight}
                columns={table?.columns ?? []}
                rows={rows}
                pageNo={page}
                limit={pageSize}
                totalCount={total}
                sortableColumns={sortWhitelistForView(view)}
                sortBy={table?.sort?.column}
                sortDir={table?.sort?.dir}
                onSort={(column) => void store.changeSort(view.id, column)}
                onPageChange={(_event, nextPage) =>
                  void store.changePage(view.id, nextPage)
                }
                onRowClick={handleRowClick}
                onRecipientClick={
                  view.kind === "recipient"
                    ? undefined
                    : (kind, id, name) =>
                        void store.openRecipient(kind, id, name)
                }
              />
            </Box>
          ) : null}
        </>
      ) : null}
    </Box>
  );
};

export default observer(AlertsListPanel);
